import React, { useState, useCallback, useEffect } from "react";
import { 
  Collapsible, 
  CollapsibleContent, 
  CollapsibleTrigger 
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp, ChevronsDown, ChevronsUp, Info, Building2, FileText, Package, Calendar, Globe, Tag, DollarSign, ListCheck, Award } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { 
  transformTenderData, 
  TenderSection, 
  TenderField 
} from "@/utils/tenderDataTransformer";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useTranslation as useTranslationHook } from "@/hooks/useTranslation";
import { useTranslation } from "react-i18next";
import { LanguageSelector } from "@/components/translation/LanguageSelector";
import { TranslateButton } from "@/components/translation/TranslateButton";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { getProcedureType, getProcedureTypeDescription, getProcedureDetailedDescription, isContractAwardNotice } from "@/utils/procedureTypeUtils";
import { AutoAcronymDetector } from "@/components/ui/acronym-tooltip";

interface TenderDataViewProps {
  tender: any;
}

export const TenderDataView: React.FC<TenderDataViewProps> = ({ tender }) => {
  console.log("Rendering tender data:", tender?.publication_number || tender?.["publication-number"]);
  
  const sections = React.useMemo(() => transformTenderData(tender), [tender]);
  
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [translationProgress, setTranslationProgress] = useState<{current: number, total: number} | null>(null);
  
  const [selectedLanguage, setSelectedLanguage] = useState<string>("EN");
  const [translatedContent, setTranslatedContent] = useState<Record<string, Record<string, string>>>({});
  const { translateText, translateBatched, isTranslating, supportedLanguages } = useTranslationHook();
  const { t, i18n } = useTranslation("tenderAgent");
  const { toast } = useToast();
  
  useEffect(() => {
    console.log("Translation state updated:", 
      Object.keys(translatedContent).length > 0 
        ? `${Object.keys(translatedContent).length} sections with translations` 
        : "No translations yet");
  }, [translatedContent]);
  
  const toggleSection = useCallback((index: number) => {
    setOpenSections(prev => ({
      ...prev,
      [index.toString()]: !prev[index.toString()]
    }));
  }, []);
  
  const toggleAllSections = useCallback(() => {
    const allOpen = sections.length > 0 && 
      sections.every((_, index) => openSections[index.toString()] === true);
    
    const newOpenState: Record<string, boolean> = {};
    sections.forEach((_, index) => {
      newOpenState[index.toString()] = !allOpen;
    });
    
    setOpenSections(newOpenState);
  }, [sections, openSections]);

  const handleLanguageChange = useCallback((language: string) => {
    console.log(`Language changed to: ${language}`);
    setSelectedLanguage(language);
    
    setTranslatedContent({});
    
    toast({
      title: i18n.language === "da" ? "Sprog ændret" : "Language changed",
      description: i18n.language === "da" ? `Valgt sprog: ${language}` : `Selected language: ${language}`,
    });
  }, [toast, i18n.language]);

  const formatLargeTextField = (text: string) => {
    if (!text || typeof text !== 'string') return text;
    
    if (text.length > 200) {
      text = text.replace(/\?\s+([A-ZÆØÅ])/g, '?\n\n$1');
      text = text.replace(/(Som defineret i)/g, '\n\n$1');
      text = text.replace(/(EU[^,\.]*?\d{4}\/\d+\/[A-Z]+[^,\.]*?\))/g, '$1\n\n');
      text = text.replace(/(EUT L \d+ af [^,\.]*?)\./g, '$1.\n\n');
      text = text.replace(/([\.!\?])\s+(Er den)/g, '$1\n\n$2');
      text = text.replace(/([\.!\?])\s+(Har den|Administreres|Således som)/g, '$1\n\n$2');
      text = text.replace(/([a-zæøå])\.\s+([A-ZÆØÅ])/g, '$1.\n\n$2');
      text = text.replace(/([a-z]\))\s+([A-ZÆØÅ])/g, '$1\n$2');
      text = text.replace(/\n{3,}/g, '\n\n');
    }
    
    return text;
  };

  const translateField = async (sectionIndex: number, fieldIndex: number, text: string) => {
    if (!text || typeof text !== 'string' || text === 'Not specified') {
      return;
    }

    console.log(`Translating field to ${selectedLanguage}:`, text.substring(0, 50) + "...");

    try {
      const result = await translateText(text, selectedLanguage);
      
      if (result.translations && result.translations.length > 0) {
        const translatedText = result.translations[0].text;
        console.log("Translation received:", translatedText.substring(0, 50) + "...");
        
        setTranslatedContent(prev => {
          const sectionKey = `${sectionIndex}`;
          const fieldKey = `${sectionIndex}-${fieldIndex}`;
          
          const newState = {...prev};
          
          if (!newState[sectionKey]) {
            newState[sectionKey] = {};
          }
          
          newState[sectionKey][fieldKey] = translatedText;
          
          return newState;
        });

        toast({
          title: i18n.language === "da" ? "Oversættelse fuldført" : "Translation complete",
          description: i18n.language === "da" ? "Feltet er blevet oversat" : "The field has been translated",
        });
      }
    } catch (error) {
      console.error("Error translating field:", error);
      toast({
        title: i18n.language === "da" ? "Oversættelse mislykkedes" : "Translation failed",
        description: i18n.language === "da" ? "Kunne ikke oversætte feltet. Prøv venligst igen." : "Failed to translate the field. Please try again.",
        variant: "destructive",
      });
    }
  };

  const extractTextToTranslate = (field: TenderField): string | null => {
    if (field.value === null || field.value === undefined || field.value === 'Not specified') {
      return null;
    }
    
    if (typeof field.value === 'string') {
      const stringValue = field.value.trim();
      
      if (
        stringValue.length === 0 || 
        stringValue === 'Invalid date' ||
        /^[A-Z0-9_-]{1,10}$/.test(stringValue) || 
        /^[0-9.,\s-]+$/.test(stringValue) ||
        /^https?:\/\//.test(stringValue) ||
        /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(stringValue) ||
        /^\d{4}-\d{2}-\d{2}T/.test(stringValue)
      ) {
        return null;
      }
      
      return stringValue;
    }
    
    if (typeof field.value === 'object' && field.value !== null) {
      if (field.type === 'multilingual') {
        const firstLanguageValue = Object.values(field.value)[0];
        if (Array.isArray(firstLanguageValue)) {
          return firstLanguageValue.join(' ').trim().length > 0 ? firstLanguageValue.join(' ') : null;
        } else if (typeof firstLanguageValue === 'string') {
          return firstLanguageValue.trim().length > 0 ? firstLanguageValue : null;
        }
      }
      
      if (Array.isArray(field.value)) {
        if (field.value.every(item => typeof item === 'string' && (/^[A-Z0-9_-]{1,15}$/.test(item) || /^[0-9.,\s-]+$/.test(item)))) {
          return null;
        }
        
        const textItems = field.value
          .filter(item => typeof item === 'string' && item.trim().length > 0 && !/^https?:\/\//.test(item))
          .join(', ');
        
        return textItems.trim().length > 0 ? textItems : null;
      }
      
      const specialFields = [
        'additional-information-lot',
        'Additional Information Lot',
        'selection-criterion-description-lot',
        'Selection Criterion Description Lot',
        'description-lot',
        'Description Lot',
        'award-criterion-description-lot',
        'Award Criterion Description Lot',
        'missing-info-submission-description-lot',
        'Missing Info Submission Description Lot'
      ];
      
      if (specialFields.includes(field.originalKey) || specialFields.includes(field.label)) {
        if (typeof field.value === 'string') {
          return field.value.trim();
        }
        
        if (Array.isArray(field.value) && field.value.length > 0) {
          return field.value.join(' ').trim();
        }
        
        const langKeys = Object.keys(field.value);
        if (langKeys.length > 0) {
          const firstLangValue = field.value[langKeys[0]];
          if (typeof firstLangValue === 'string') {
            return firstLangValue.trim();
          }
          if (Array.isArray(firstLangValue) && firstLangValue.length > 0) {
            return firstLangValue.join(' ').trim();
          }
        }
      }
    }
    
    return null;
  };

  const translateAllFields = async () => {
    console.log("Starting translation of ALL tender detail fields");
    if (isTranslating) return;
    
    const prevOpenSections = {...openSections};
    const tempOpenSections: Record<string, boolean> = {};
    sections.forEach((_, index) => {
      tempOpenSections[index.toString()] = true;
    });
    setOpenSections(tempOpenSections);
    
    const textsToTranslate: string[] = [];
    const fieldIndices: { sectionIndex: number; fieldIndex: number }[] = [];

    sections.forEach((section, sectionIndex) => {
      section.fields.forEach((field, fieldIndex) => {
        const textToTranslate = extractTextToTranslate(field);
        
        if (textToTranslate) {
          console.log(`Adding field for translation: Section ${sectionIndex} (${section.name}), Field ${fieldIndex} (${field.label}): ${textToTranslate.substring(0, 30)}...`);
          textsToTranslate.push(textToTranslate);
          fieldIndices.push({ sectionIndex, fieldIndex });
        } else {
          console.log(`Skipping non-translatable field: Section ${sectionIndex}, Field ${fieldIndex} (${field.label}), type: ${field.type}, originalKey: ${field.originalKey}`);
        }
      });
    });

    if (textsToTranslate.length === 0) {
      toast({
        title: i18n.language === "da" ? "Intet indhold at oversætte" : "No content to translate",
        description: i18n.language === "da" ? "Intet oversat indhold fundet i dette udbud." : "No translatable content found in this tender.",
      });
      return;
    }

    console.log(`Preparing to translate ${textsToTranslate.length} fields to ${selectedLanguage}`);
    
    toast({
      title: i18n.language === "da" ? "Starter oversættelse" : "Starting translation",
      description: i18n.language === "da" 
        ? `Oversætter ${textsToTranslate.length} felter til ${selectedLanguage}. Dette kan tage et øjeblik...`
        : `Translating ${textsToTranslate.length} fields to ${selectedLanguage}. This may take a moment...`,
    });

    setTranslationProgress({ current: 0, total: textsToTranslate.length });

    try {
      const batchSize = 30;
      
      const updateProgress = (current: number, total: number) => {
        setTranslationProgress({ current, total });
      };
      
      const result = await translateBatched(textsToTranslate, selectedLanguage, batchSize, updateProgress);
      
      if (!result.translations || result.translations.length === 0) {
        toast({
          title: i18n.language === "da" ? "Oversættelse mislykkedes" : "Translation failed",
          description: i18n.language === "da" ? "Ingen oversættelser blev returneret. Prøv venligst igen." : "No translations were returned. Please try again.",
          variant: "destructive",
        });
        return;
      }
      
      const newTranslatedContent: Record<string, Record<string, string>> = {};
      
      result.translations.forEach((translation, index) => {
        if (!translation || !translation.text || translation.detected_source_language === "SKIPPED") {
          return;
        }
        
        const { sectionIndex, fieldIndex } = fieldIndices[index];
        const sectionKey = `${sectionIndex}`;
        const fieldKey = `${sectionIndex}-${fieldIndex}`;
        
        if (!newTranslatedContent[sectionKey]) {
          newTranslatedContent[sectionKey] = {};
        }
        
        newTranslatedContent[sectionKey][fieldKey] = translation.text;
      });
      
      setTranslatedContent(prevContent => ({
        ...prevContent,
        ...newTranslatedContent
      }));
      
      const translatedFieldsCount = Object.values(newTranslatedContent).reduce(
        (acc, section) => acc + Object.keys(section).length, 0
      );
      
      toast({
        title: i18n.language === "da" ? "Oversættelse fuldført" : "Translation complete",
        description: i18n.language === "da" 
          ? `Oversatte ${translatedFieldsCount} felter til ${selectedLanguage}`
          : `Successfully translated ${translatedFieldsCount} fields to ${selectedLanguage}`,
      });
      
      setOpenSections(prevOpenSections);
      
    } catch (error) {
      console.error("Error in batch translation:", error);
      toast({
        title: i18n.language === "da" ? "Oversættelse mislykkedes" : "Translation failed",
        description: i18n.language === "da" 
          ? "Kunne ikke oversætte alle felter. Nogle er muligvis blevet oversat."
          : "Failed to translate all fields. Some may have been translated.",
        variant: "destructive",
      });
    } finally {
      setTranslationProgress(null);
    }
  };

  const getFieldContent = (sectionIndex: number, fieldIndex: number, originalText: unknown): unknown => {
    const sectionKey = `${sectionIndex}`;
    const fieldKey = `${sectionIndex}-${fieldIndex}`;
    
    const hasTranslation = translatedContent[sectionKey]?.[fieldKey];
    
    if (hasTranslation) {
      return translatedContent[sectionKey][fieldKey];
    }
    
    return originalText;
  };

  const getSectionIcon = (sectionName: string) => {
    switch (sectionName.toLowerCase()) {
      case "buyer information":
      case "indkøberinformation":
        return <Building2 className="h-5 w-5" />;
      case "procedure details":
      case "proceduredetaljer":
        return <FileText className="h-5 w-5" />;
      case "lot and award information":
      case "lot- og tildelingsinformation":
        return <Package className="h-5 w-5" />;
      case "deadlines & timing":
      case "deadlines & timing":
        return <Calendar className="h-5 w-5" />;
      case "basic information":
      case "grundlæggende information":
        return <Info className="h-5 w-5" />;
      case "submission & document links":
      case "indlevering & dokumentlinks":
        return <ListCheck className="h-5 w-5" />;
      case "classification & cpv codes":
      case "klassifikation & cpv-koder":
        return <Tag className="h-5 w-5" />;
      case "place of performance":
      case "udførelsessted":
        return <Globe className="h-5 w-5" />;
      case "value & financial details":
      case "værdi & finansielle detaljer":
        return <DollarSign className="h-5 w-5" />;
      default:
        return <Info className="h-5 w-5" />;
    }
  };

  const renderProcedureType = (tender: any) => {
    const procedureType = getProcedureType(tender);
    const procedureTypeDescription = procedureType ? getProcedureTypeDescription(procedureType) : i18n.language === "da" ? 'Ukendt proceduretype' : 'Unknown procedure type';
    const detailedDescription = procedureType ? getProcedureDetailedDescription(procedureType) : '';

    if (!procedureType) return null;

    return (
      <div className="mb-4">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-sm cursor-help">
                <FileText className="h-3.5 w-3.5 mr-1" />
                {procedureTypeDescription}
              </Badge>
            </TooltipTrigger>
            <TooltipContent className="max-w-sm">
              <p>{detailedDescription}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  };

  if (!tender || Object.keys(tender).length === 0) {
    return <div className="text-gray-500 italic">
      {i18n.language === "da" ? "Ingen detaljerede data tilgængelige for dette udbud" : "No detailed data available for this tender"}
    </div>;
  }

  const orderedSections = [...sections].sort((a, b) => {
    const order = [
      "Basic Information",
      "Buyer Information",
      "Procedure Details",
      "Classification & CPV Codes",
      "Deadlines & Timing",
      "Lot and Award Information", 
      "Submission & Document Links",
      "Value & Financial Details",
      "Place of Performance",
      "Legal & Regulatory Information",
      "Additional Information"
    ];
    
    const indexA = order.indexOf(a.name);
    const indexB = order.indexOf(b.name);
    
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }
    
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    
    return a.name.localeCompare(b.name);
  });

  return (
    <div className="my-6">
      <div className="flex justify-between mb-4">
        <div className="flex gap-2 items-center">
          <LanguageSelector 
            selectedLanguage={selectedLanguage}
            onLanguageChange={handleLanguageChange}
          />
          <Button
            variant="outline"
            size="sm"
            onClick={translateAllFields}
            disabled={isTranslating}
            className="flex items-center gap-2"
          >
            {isTranslating ? (
              <>
                <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                {translationProgress ? 
                  `${Math.round((translationProgress.current / translationProgress.total) * 100)}%` : 
                  (i18n.language === "da" ? 'Oversætter...' : 'Translating...')}
              </>
            ) : (
              <>
                <Globe className="h-4 w-4" />
                {i18n.language === "da" ? "Oversæt alle felter" : "Translate All Fields"}
              </>
            )}
          </Button>
        </div>
        <Button 
          variant="outline" 
          size="sm" 
          onClick={toggleAllSections}
          className="flex items-center gap-2"
        >
          {sections.length > 0 && 
           sections.every((_, index) => openSections[index.toString()] === true) ? (
            <>
              <ChevronsUp className="h-4 w-4" />
              {i18n.language === "da" ? "Sammenfold alle" : "Collapse All"}
            </>
          ) : (
            <>
              <ChevronsDown className="h-4 w-4" />
              {i18n.language === "da" ? "Udvid alle" : "Expand All"}
            </>
          )}
        </Button>
      </div>
      
      {renderProcedureType(tender)}
      
      {isContractAwardNotice(tender) && (
        <div className="mb-4">
          <Badge variant="success" className="text-sm">
            <Award className="h-3.5 w-3.5 mr-1" />
            {i18n.language === "da" ? "Kontrakttildeling" : "Contract Award Notice"}
          </Badge>
        </div>
      )}
      
      <div className="space-y-3">
        {orderedSections.map((section, sectionIndex) => {
          let sectionName = section.name;
          if (i18n.language === "da") {
            const sectionKey = section.name.toLowerCase();
            if (sectionKey === "basic information") {
              sectionName = t("tenderView.sectionTitles.basicInformation");
            } else if (sectionKey === "buyer information") {
              sectionName = t("tenderView.sectionTitles.buyerInformation");
            } else if (sectionKey === "procedure details") {
              sectionName = t("tenderView.sectionTitles.procedureDetails");
            } else if (sectionKey === "classification & cpv codes") {
              sectionName = t("tenderView.sectionTitles.classificationCPV");
            } else if (sectionKey === "deadlines & timing") {
              sectionName = t("tenderView.sectionTitles.deadlinesTiming");
            } else if (sectionKey === "lot and award information") {
              sectionName = t("tenderView.sectionTitles.lotAward");
            } else if (sectionKey === "submission & document links") {
              sectionName = t("tenderView.sectionTitles.submissionDocuments");
            } else if (sectionKey === "value & financial details") {
              sectionName = t("tenderView.sectionTitles.valueFinancialDetails");
            } else if (sectionKey === "place of performance") {
              sectionName = t("tenderView.sectionTitles.placePerformance");
            } else if (sectionKey === "legal & regulatory information") {
              sectionName = t("tenderView.sectionTitles.legalRegulatory");
            } else if (sectionKey === "additional information") {
              sectionName = t("tenderView.sectionTitles.additionalInfo");
            }
          }
          
          return (
            <Collapsible 
              key={sectionIndex} 
              className="border rounded-lg bg-white shadow-sm"
              open={openSections[sectionIndex.toString()] || false}
            >
              <CollapsibleTrigger asChild>
                <div 
                  className="p-4 border-b bg-muted/30 flex items-center justify-between cursor-pointer hover:bg-muted/50 transition-colors"
                  onClick={() => toggleSection(sectionIndex)}
                >
                  <div className="flex items-center gap-3">
                    {getSectionIcon(section.name)}
                    <h3 className="font-medium">
                      <span className="mr-1">{sectionIndex + 1}.</span>
                      {sectionName} <span className="text-gray-500 text-xs">
                        ({section.fields.length} {i18n.language === "da" ? "felter" : "fields"})
                      </span>
                    </h3>
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                    {openSections[sectionIndex.toString()] ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                    <span className="sr-only">Toggle</span>
                  </Button>
                </div>
              </CollapsibleTrigger>
              
              <CollapsibleContent>
                <div className="p-4 space-y-4">
                  {section.fields.map((field, fieldIndex) => {
                    const sectionKey = `${sectionIndex}`;
                    const fieldKey = `${sectionIndex}-${fieldIndex}`;
                    const hasTranslation = translatedContent[sectionKey]?.[fieldKey];
                    
                    const displayValue = getFieldContent(sectionIndex, fieldIndex, field.value);
                    const canTranslate = extractTextToTranslate(field) !== null;
                    
                    return (
                      <div key={fieldIndex} className="border-b pb-3 last:border-0 last:pb-0">
                        <div className="flex justify-between items-start mb-1">
                          <div className="text-sm font-medium text-gray-700 flex items-center">
                            <span className="mr-1">{sectionIndex + 1}.{fieldIndex + 1}</span>
                            {field.label}
                            
                            {field.originalKey && (
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button variant="ghost" size="sm" className="h-5 w-5 p-0 ml-1">
                                      <Info className="h-3 w-3 text-gray-400" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p className="text-xs">
                                      {i18n.language === "da" ? "Felt-ID" : "Field ID"}: {field.originalKey}
                                    </p>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>
                          
                          {canTranslate && (
                            <TranslateButton
                              tooltipText={i18n.language === "da" ? `Oversæt til ${selectedLanguage}` : `Translate to ${selectedLanguage}`}
                              loading={isTranslating}
                              size="sm"
                              variant="ghost"
                              onClick={() => translateField(sectionIndex, fieldIndex, extractTextToTranslate(field) || "")}
                            />
                          )}
                        </div>
                        
                        {isTranslating && translationProgress ? (
                          <Skeleton className="h-6 w-full" />
                        ) : field.type === "array" ? (
                          <div className="flex flex-wrap gap-2">
                            {Array.isArray(displayValue) && displayValue.length > 0 ? (
                              displayValue.map((item: any, itemIndex: number) => (
                                <Badge key={itemIndex} variant="outline">
                                  {typeof item === 'object' ? JSON.stringify(item) : String(item)}
                                </Badge>
                              ))
                            ) : (
                              <span className="text-sm text-gray-500">{String(displayValue)}</span>
                            )}
                          </div>
                        ) : field.type === "url" ? (
                          <a 
                            href={String(displayValue)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-blue-600 hover:underline break-words inline-flex items-center"
                          >
                            {String(displayValue)}
                          </a>
                        ) : field.type === "boolean" ? (
                          <div className="text-sm">
                            <Badge variant={displayValue === "Yes" || displayValue === "Ja" ? "success" : "secondary"}>
                              {String(displayValue)}
                            </Badge>
                          </div>
                        ) : field.type === "coded" ? (
                          <div className="text-sm">
                            <div className="flex flex-col space-y-1">
                              <Badge variant="outline" className="w-fit">
                                {String(displayValue)}
                              </Badge>
                              {field.codeDescription && field.codeDescription !== String(displayValue) && (
                                <span className="text-gray-700 mt-1">{field.codeDescription}</span>
                              )}
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm break-words whitespace-pre-wrap leading-relaxed">
                            {typeof displayValue === 'string' ? (
                              <AutoAcronymDetector text={formatLargeTextField(displayValue)} />
                            ) : (
                              String(displayValue)
                            )}
                            
                            {hasTranslation && field.value && (
                              <div className="text-xs text-blue-500 mt-1">
                                ({i18n.language === "da" ? "Oversat fra" : "Translated from"} {typeof field.value === 'string' 
                                  ? field.value.substring(0, 20) + "..." 
                                  : "original"})
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CollapsibleContent>
            </Collapsible>
          );
        })}
      </div>
    </div>
  );
};

export default TenderDataView;
